import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { IS_LOCKED } from '../../apiBase';
import { useLanguage } from '../../contexts/LanguageContext';
import { CallBadge, type Choice } from './CallBadge';
import { StepPromptNav } from './StepPromptNav';
import { useAssemblySteps } from './categoryBands';
import { CoverFirstPanel, useJointCoverFacts, type JointCoverFacts } from './CoversRun';
import { createDocValueStore } from './docStore';
import { HolesFirstPanel, useCanFind, type useHoles } from './FindHoles';
import { holeIdsLabel, useJoints, type Joint } from './holeGroups';
import {
  findStepHoles, heldHoles, holesOfGroup, holesOfPart, keepHoles, stopStepHoles, useStepHoles, type HeldHoles,
} from './stepHolesRun';
import { drillPart, said, type DrillProblem } from './drillParts';
import { holdMesh, releaseMesh, restoreMesh } from './threeDApi';
import { invalidatePartModels } from './partModels';
import type { ByPart } from './partModelSize';
import {
  COVERS_MODEL, heldCovers, keepCovers, runCoverPlacement, useCoverPlacements, type JointCovers,
} from './coverPlacementRun';
import { STEPS_MODEL } from './models';
import { useSince } from './waitClock';
import { usePlace } from './lastPlace';

// HOLES & COVERS — Find holes and Place the covers as one badge, run JOINT BY JOINT in manual
// order (see Joint): a hardware joint opened, its holes found — one pass — and its parts drilled
// with every hole they have (see drillParts); a covers joint opened and its covers placed — one
// pass — on the holes of the joint whose screws it caps, or on a frame's feet. Buttons come after
// their screws in the manual, so the holes they sit on are found by then, and Find holes still
// tells each joint what the ones before it found.
//
// ONE RUN PER MANUAL, HELD HERE AND NOT IN A COMPONENT. It was the joint view's: leaving the
// subphase unmounted it, coming back mounted a new one, and the new one — seeing joints still
// queued — started a second run beside the first, which carried on against a scene that was gone.
// The run needs a scene (a joint opened, its covers' facts read off it): ITS OWN, a second joint view
// off the screen (see JointRunScene), which REGISTERS itself as it (see JointView) — so the joint
// view you read is free to walk while it works. Where none is mounted (no models phase to hold
// one), the run waits for it.
//
// STOP PUTS THE JOINT BEING WORKED ON BACK AS IT WAS, and keeps every joint finished before it.
// The call out is aborted — its answer is thrown away, though the server still finishes it and it
// is still billed — and a Blender drill already running is waited for, since it would otherwise
// write its mesh after the restore. Then the joint's answers (holes, covers, drill notes) are put
// back from the copy taken when it started, and its parts' meshes from the copies the server set
// aside before each was first touched (see Hold): file copies, no re-drill.

// THE JOINTS A PRESS ASKED FOR, in order; `total` is how many, which the Run row counts through;
// `startedAt` and `took` are its clock, kept with it so leaving and coming back does not restart
// it — `took` the last run that went to its end: a stopped one leaves the one before it standing;
// it; `waiting` is a run held for the joints view to be on screen.
export type JointRequests = {
  queue: string[]; total: number; startedAt: number | null; took: number | null; waiting: boolean;
  // Which half of the joint is out, for the Run row: the drill is the one step with no call of its own.
  drilling: boolean;
  // Stop pressed, and the joint being put back.
  rolling: boolean;
  // THE JOINT BEING WORKED ON, from its start until it is done or put back — through a Stop, which
  // empties the queue while the joint it was on is still being restored.
  current: string | null;
};
const IDLE: JointRequests = {
  queue: [], total: 0, startedAt: null, took: null, waiting: false, drilling: false, rolling: false, current: null,
};
const requests = createDocValueStore<JointRequests>(IDLE);
export const useJointRequests = requests.useValue;

// WHETHER THE RUN IS AT WORK ON ONE KIND OF JOINT — the hardware ones (Drilling holes, the 3D
// models phase) or the covers ones (Placing covers, the Assembly phase): on one of them, or still
// to reach one. One run serves both, and each says it is working only for its own joints.
export function useJointsRunning(docId: string, kind: 'holes' | 'covers'): boolean {
  const run = useJointRequests(docId);
  const joints = useJoints(docId);
  const ids = [run.current, ...run.queue];
  return joints.some((j) => j.finds === (kind === 'holes') && ids.includes(j.id));
}
const patch = (docId: string, p: Partial<JointRequests>) => requests.set(docId, { ...requests.get(docId), ...p });

// WHAT THE RUN NEEDS OF THE JOINT VIEW ON SCREEN: which joint is open and a way to open another,
// what the page holds (refreshed every render), and the look at a first covers answer.
export type JointView = {
  jointId: string | undefined;
  everyJoint: Joint[];
  coverFacts: JointCoverFacts[];
  holesBusy: boolean;
  run: ReturnType<typeof useHoles>;
  models: ByPart;
  // THE PARTS' MODELS ARE READ: false from a drilling's re-read (see invalidatePartModels) until it
  // lands, while `run.meshOf` answers null for every part.
  modelsLoaded: boolean;
  open: (id: string) => void;
};
const views = new Map<string, { token: object; view: JointView }>();
export function registerJointView(docId: string, token: object, view: JointView) {
  views.set(docId, { token, view });
}
export function dropJointView(docId: string, token: object) {
  if (views.get(docId)?.token === token) views.delete(docId);
}

// STOPPED IS AN EMPTY QUEUE: nothing more is asked — and the call out is aborted (see Stop).
const stopped = (docId: string) => !requests.get(docId).queue.length;
// The joint being worked on's abort, by manual.
const aborts = new Map<string, AbortController>();

// WHAT A JOINT HAD BEFORE THE RUN TOUCHED IT: its answers copied when it starts, and each part's
// mesh set aside on the server the first time the run drills it (by job id, the part it is).
type Hold = {
  holes: HeldHoles;
  covers: Record<string, JointCovers>;
  notes: Record<string, DrillProblem[]>;
  meshes: Map<number, string>;
};

// WAIT FOR `get`: indefinitely while no joints view is on screen (said on the Run row), and for
// `ms` of it being there — a scene that never yields what is asked is not waited on for good.
async function until<T>(docId: string, get: (v: JointView) => T | null | undefined | false, ms = 30_000): Promise<T | null> {
  let left = ms;
  for (;;) {
    if (stopped(docId)) return null;
    const v = views.get(docId)?.view;
    patch(docId, { waiting: !v });
    if (v) {
      const got = get(v);
      if (got) return got;
      if ((left -= 100) <= 0) return null;
    }
    await new Promise((r) => setTimeout(r, 100));
  }
}

// ONE JOINT: opened, and its holes found and drilled, or its covers placed — see the file's head.
async function runJoint(docId: string, id: string, file: Blob, t: (k: string) => string, hold: Hold, signal: AbortSignal) {
  const first = await until(docId, (v) => v.modelsLoaded && v);
  const j = first?.everyJoint.find((x) => x.id === id);
  if (!first || !j) return;
  first.open(j.id);
  const open = await until(docId, (v) => v.jointId === j.id && v);
  if (!open) return;
  if (!j.finds) {
    // A COVERS JOINT: its covers, on the holes and feet of the scene — a beat for them to reach the
    // facts, and the feet read off the models once they are back, which takes a while.
    await new Promise((r) => setTimeout(r, 300));
    const f = await until(docId, (v) => v.jointId === j.id && v.modelsLoaded && !v.holesBusy && v.coverFacts.find((c) => c.joint.id === j.id), 120_000);
    if (!f || stopped(docId)) return;
    await runCoverPlacement(docId, j.id, f.facts, COVERS_MODEL, signal);
    // AND HOW DEEP THEY GO INTO THE WOOD, and which have none under them, measured by the run's
    // scene before it moves on (see keepCoverInto, keepCoverMissed): the joint's tab counts its covers' problems off those depths, and a joint
    // left before they were measured showed none until somebody opened it.
    await until(docId, (v) => {
      const c = heldCovers(docId)[j.id];
      return v.jointId === j.id && (!c?.placements?.length || (!!c.into && !!c.missed));
    }, 60_000);
    return;
  }
  // ITS PARTS UNDRILLED FIRST: the old answer's bores are in the files, and would stand in the
  // view — and in the wood Find holes measures — until the new ones replace them.
  await drillJoint(docId, j, hold, true);
  if (stopped(docId)) return;
  {
    // THE VIEW AS IT IS NOW, ITS MODELS READ: the drilling just done read them all again, and a
    // view taken before it lands answers no model for any part — the question went out with no
    // part placed in the product, no outline, no pictures.
    const now = await until(docId, (v) => v.jointId === j.id && v.modelsLoaded && !v.holesBusy && v);
    if (!now) return;
    const r = now.run;
    await findStepHoles(docId, file, r.steps, r.st.subSteps, r.items, STEPS_MODEL, t,
      { idx: j.i, letters: j.group.map((sub) => sub.letter) }, r.meshOf, r.interactions, signal);
  }
  // ITS PARTS DRILLED, with every hole each has — from this joint and every other — from scratch
  // (see drillParts): before the covers, whose collisions are measured against the wood.
  await drillJoint(docId, j, hold);
}

// WHAT DRILLING A PART LEFT UNDONE, by part id — each joint showing the part says its own holes'
// and the part's own (see DrillProblem) — replaced by the part's next drilling.
const drillNotes = createDocValueStore<Record<string, DrillProblem[]>>({});
export const useDrillNotes = drillNotes.useValue;

// `bare`: every bore taken out and none cut — the run's first word on the joint's parts.
async function drillJoint(docId: string, j: Joint, hold: Hold, bare = false) {
  const v = await until(docId, (x) => x.modelsLoaded && !x.holesBusy && x);
  if (!v) return;
  const { run, models } = v;
  const own = holesOfGroup(run.holes.byStep, run.steps[j.i]?.category ?? '', j.group);
  const parts = [...new Set([...j.drilled, ...own.map((h) => h.part)])];
  patch(docId, { drilling: true });
  try {
    for (const part of parts) {
      if (stopped(docId)) return;
      const job = run.meshOf(part);
      const item = run.items[Number(part)];
      if (!job || !item) continue;
      // SET ASIDE BEFORE IT IS FIRST TOUCHED, or not touched: a part Stop could not put back is
      // left as it stands, and said.
      if (!hold.meshes.has(job.id)) {
        try {
          await holdMesh(job.id);
          hold.meshes.set(job.id, part);
        } catch (e) {
          drillNotes.set(docId, { ...drillNotes.get(docId), [part]: [{ holes: [], what: `not drilled: its mesh could not be set aside for Stop (${said(e)})` }] });
          continue;
        }
      }
      const holes = bare ? [] : holesOfPart(run.holes.byStep, part).map((h) => h.hole);
      const drilled = await drillPart(job, item, holes, run.items, models);
      const problems = [...drilled.problems];
      drillNotes.set(docId, { ...drillNotes.get(docId), [part]: problems });
    }
  } finally {
    patch(docId, { drilling: false });
    // THE DRILLED MODELS, read again: every view showing one of these parts draws the new bores.
    invalidatePartModels();
  }
}


// THE JOINT PUT BACK, after Stop: its answers, then its parts' meshes.
async function rollBack(docId: string, hold: Hold) {
  patch(docId, { rolling: true });
  try {
    keepHoles(docId, hold.holes);
    keepCovers(docId, hold.covers);
    const notes = { ...hold.notes };
    await Promise.all([...hold.meshes].map(async ([job, part]) => {
      try {
        await restoreMesh(job);
      } catch (e) {
        notes[part] = [{ holes: [], what: `Stop could not put its earlier mesh back: ${said(e)}` }];
      }
    }));
    drillNotes.set(docId, notes);
  } finally {
    patch(docId, { rolling: false });
    invalidatePartModels();
  }
}

// THE JOINT DONE: what was set aside for it goes. One that fails is swept by the server.
const letGo = (hold: Hold) => Promise.all([...hold.meshes.keys()].map((job) => releaseMesh(job).catch(() => {})));

// THE PRESS: the joints, one after another — and a press while a run is going is not a second run.
function askJoints(docId: string, ids: string[], file: Blob, t: (k: string) => string) {
  const was = requests.get(docId);
  if (was.queue.length || was.rolling) return;
  const startedAt = Date.now();
  requests.set(docId, { ...IDLE, queue: ids, total: ids.length, startedAt, took: was.took });
  let cut = false;
  void (async () => {
    try {
      for (;;) {
        const id = requests.get(docId).queue[0];
        if (!id) break;
        const abort = new AbortController();
        aborts.set(docId, abort);
        const hold: Hold = {
          holes: heldHoles(docId), covers: heldCovers(docId), notes: drillNotes.get(docId), meshes: new Map(),
        };
        patch(docId, { current: id });
        try {
          await runJoint(docId, id, file, t, hold, abort.signal);
        } finally {
          aborts.delete(docId);
          await (abort.signal.aborted ? rollBack(docId, hold) : letGo(hold));
          patch(docId, { current: null });
        }
        const now = requests.get(docId);
        // Stopped: in a joint (put back above), or between two — nothing to put back there, and
        // still not a run that finished.
        if (abort.signal.aborted || !now.queue.length) {
          cut = true;
          break;
        }
        if (now.queue[0] === id) patch(docId, { queue: now.queue.slice(1) });
      }
    } finally {
      patch(docId, {
        queue: [], waiting: false, rolling: false, current: null, startedAt: null,
        took: cut ? was.took : Math.round((Date.now() - startedAt) / 1000),
      });
    }
  })();
}

// WHERE THE JOINT VIEW REMEMBERS ITS OPEN TAB, per kind of joint.
export const jointPlaceSlot = (kind: 'holes' | 'covers' | undefined) => (kind === 'covers' ? 'coverJoint' : 'joint');

export function JointsRun({ docId, file, locked, kind }: {
  docId: string; file: Blob; locked?: boolean;
  // The subphase's joints: Drilling holes the hardware ones, Placing covers the covers ones.
  kind: 'holes' | 'covers';
}) {
  const { t } = useLanguage();
  const { steps } = useAssemblySteps(docId);
  const joints = useJoints(docId).filter((j) => j.finds === (kind === 'holes'));
  // ITS OWN BADGE, named as its subphase is.
  const title = t(kind === 'holes' ? 'notice.models.subComponents' : 'notice.assembly.subCovers');
  const facts = useJointCoverFacts(docId);
  const covers = useCoverPlacements(docId);
  const holes = useStepHoles(docId);
  const queued = useJointRequests(docId);
  const since = useSince(queued.startedAt);
  const { canFind, why } = useCanFind(docId, locked);
  const working = useJointsRunning(docId, kind);

  // THE JOINT WHOSE TAB IS OPEN is the one the window opens on — walked from there by its arrows.
  const [tab] = usePlace(docId, jointPlaceSlot(kind));
  const [walk, setWalk] = useState(0);
  useEffect(() => {
    const k = joints.findIndex((j) => j.id === tab);
    if (k >= 0) setWalk(k);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, joints.map((j) => j.id).join(',')]);
  const at = Math.min(walk, Math.max(0, joints.length - 1));
  const shown = joints[at] ?? null;
  if (!shown) {
    return why ? <p className="text-center text-xs text-slate-400">{t(why)}</p> : null;
  }

  // ONE RUN AT A TIME, whichever kind it is on; what is said working is this badge's own.
  const going = queued.queue.length > 0 || queued.rolling;
  const ask = (ids: string[]) => askJoints(docId, ids, file, t);
  const nth = queued.total - queued.queue.length + 1;

  // THE ROW OF THE JOINT WALKED TO: its holes, or its covers — a joint is one or the other.
  //
  // KEYED BY THE WINDOW, NOT THE JOINT: the badge finds the open window again by its key, and a key
  // that moved with the arrows was a window that closed on them. Each window's arrows walk only the
  // joints that have it — a window walked onto a joint without one would close all the same.
  const navOver = (has: (j: Joint) => boolean) => {
    const list = joints.filter(has);
    return (
      <StepPromptNav
        steps={list.map((j) => steps[j.i]?.num ?? 0)}
        // AS THE JOINT'S TAB NAMES IT: its hole ids, then its component parts.
        labelOf={(i) => [holeIdsLabel(list[i]), list[i].parts].filter(Boolean).join('\n')}
        at={Math.max(0, list.findIndex((j) => j.id === shown.id))}
        onGo={(n) => setWalk(joints.indexOf(list[n]))}
      />
    );
  };
  const holesNav = navOver((j) => j.finds);
  const coversNav = navOver((j) => facts.some((f) => f.joint.id === j.id));
  const mine = facts.find((f) => f.joint.id === shown.id);
  const rows: Choice[] = [
    ...(shown.finds ? [
      { key: 'holes', icon: <FileText className="h-3.5 w-3.5" />, label: t('notice.tip.menuPromptAll'),
        panel: <HolesFirstPanel key={shown.id} docId={docId} file={file} joint={shown} />, header: holesNav },
    ] : []),
    ...(mine ? [
      { key: 'covers', icon: <FileText className="h-3.5 w-3.5" />, label: t('notice.tip.menuPromptAll'),
        panel: <CoverFirstPanel key={shown.id} docId={docId} unit={mine} />, header: coversNav },
    ] : []),
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-2">
      {why && <p className="basis-full text-center text-xs text-slate-400">{t(why)}</p>}
      <CallBadge
        title={title}
        named
        noPrompt
        promptLabel=""
        load={async () => ({ prompt: '' })}
        revision={shown.id}
        extra={rows}
        run={{
          label: `${t('notice.tip.menuRun')}${working && queued.total > 1 ? ` · ${nth}/${queued.total}` : ''}${
            working && queued.drilling ? ` · ${t('notice.joints.drilling')}` : ''}${
            working && queued.waiting ? ` · ${t('notice.joints.waiting')}` : ''}${
            queued.rolling ? ` · ${t('notice.joints.rollingBack')}` : ''}`,
          onRun: () => ask(joints.map((j) => j.id)),
          busy: working,
          // STOP: nothing more is asked, the call out is aborted, and the joint is put back as it
          // was. Rolling back from the press: a drill already out is waited for first, and the run
          // is not free again until the joint is back.
          onStop: () => {
            patch(docId, { queue: [], rolling: true });
            stopStepHoles(docId);
            aborts.get(docId)?.abort();
          },
          stopping: queued.rolling ? t('notice.joints.rollingBack')
            : holes.stopping ? t('notice.holes.stopping') : undefined,
          elapsed: working ? since : queued.took,
          disabled: !!locked || IS_LOCKED || going || !canFind,
          options: joints.map((j) => ({ key: j.id, label: j.parts })),
          // TICKED, THEN RUN: several joints in one run, always in manual order (see askJoints).
          many: {
            all: t('notice.joints.runAll'),
            run: (n) => t(n === 1 ? 'notice.joints.runOne' : 'notice.joints.runMany').replace('{n}', String(n)),
            onRun: ask,
          },
        }}
      />
      {(holes.error || covers.error) && (
        <span className="basis-full text-center text-[11px] text-rose-500">{holes.error || covers.error}</span>
      )}
    </div>
  );
}
